import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform } from '@ionic/angular';
// environments
import { environment } from '../../../environments/environment';
// rxjs
import { Observable, Subject } from 'rxjs';
import {filter, map, share} from 'rxjs/operators';
// models
import { Customer, CustomerToken } from '../models/customer.model';
import { CreateSubscriptionData } from '../models/order.model';
// services
import { RouteService } from './route.service';
// stores
import {RestaurantStore} from '../stores/restaurant.store';
import { NO_SELECTED_CARD, STORAGE_CUSTOMER_DATA, STORAGE_TAB_DATA } from '../constants';
import {CartStore} from '../stores/cart.store';
import {StorageService} from './storage.service';
import {PaymentStore} from '../stores/payment.store';
import { GroupOrderTab, OrderTab, PaymentSources, SavedPaymentState } from '../models/payment-item.model';
import {OrderApiService} from './order.api.service';
// constants
import { v4 as uuid4 } from 'uuid';
import { dataToCustomer, dataToCustomerToken } from '../dto/customer.dto';
import { PartialBalance } from '../models/gift-card.model';

@Injectable()
export class PaymentService {
  constructor(
    private platform: Platform,
    private http: HttpClient,
    private routeService: RouteService,
    private restaurantStore: RestaurantStore,
    private cartStore: CartStore,
    private storageService: StorageService,
    private orderApiService: OrderApiService,
    private paymentStore: PaymentStore,
  ) {
  }

  private static readonly httpOptionsForProcessCard = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    })
  };

  public partialBalance: Subject<PartialBalance> = new Subject<PartialBalance>();

  public setPartialBalance(partialBalance: PartialBalance): void {
    this.partialBalance.next(partialBalance);
  }
  public get getPartialBalance(): Observable<PartialBalance> {
    return this.partialBalance.asObservable();
  }

  public getSavedPaymentState(checked: boolean): SavedPaymentState {
    if (!checked) {
      return new SavedPaymentState(NO_SELECTED_CARD, checked);
    }
    return new SavedPaymentState(
      this.paymentStore.savedCardId
        ? this.paymentStore.savedCardId
        : this.paymentStore.customer.cards
          ? this.paymentStore.customer.cards[0].id
          : NO_SELECTED_CARD,
      checked
    );
  }

  public getCustomerByPhone(phoneNumber: string, isSharedCardsEnabled: boolean, isNeedSharingPhoneNumber: boolean): Observable<Customer> {
    const apiVer = isSharedCardsEnabled ? 'v2/' : '';
    const apiURL = !isNeedSharingPhoneNumber ? `${environment.apiUrl}${apiVer}${this.routeService.getBaseUrl()}` : `${environment.apiUrl}${this.routeService.createGroupUrl(this.restaurantStore.group.groupId)}`;

    const body = {
      isMobilePlatform: this.platform.is('mobile')
    };
    const isNeedLoyalty = this.cartStore.isLoyaltyEnabled;
    const idempotencyKey = uuid4();

    return this.http.post(`${apiURL}/customers/phoneNumber/${phoneNumber}?requireLoyalty=${isNeedLoyalty}&idempotencyKey=${idempotencyKey}`, body)
      .pipe(
        map((customer: any) => {
          if (this.platform.is('mobile')) {
            // this.oneSignalService.setupPush(customer.phone_number);
          }
          return dataToCustomer(customer);
        }),
        share()
      );
  }

  public getVerifiedCustomerByPhone(phoneNumber: string, token: string, isSharedCardsEnabled: boolean, isNeedSharingPhoneNumber: boolean): Observable<Customer> {
    const apiVer = isSharedCardsEnabled ? 'v2/' : '';
    const apiURL = !isNeedSharingPhoneNumber ? `${environment.apiUrl}${apiVer}${this.routeService.getBaseUrl()}` : `${environment.apiUrl}${this.routeService.createGroupUrl(this.restaurantStore.group.groupId)}`;
    const isNeedLoyalty = this.cartStore.isLoyaltyEnabled;
    const idempotencyKey = uuid4();

    return this.http.post(`${apiURL}/customers/phoneNumber/${phoneNumber}/authorize?requireLoyalty=${isNeedLoyalty}&idempotencyKey=${idempotencyKey}`, token)
      .pipe(
        map((customer: any) => {
          if (this.platform.is('mobile')) {
            // this.oneSignalService.setupPush(customer.phone_number);
          }
          return dataToCustomer(customer);
        }),
        share()
      );
  }

  public getCustomerById(customerId: string): Observable<Customer> {
    const apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    return this.http.get(`${apiURL}/customers/${customerId}`).pipe(
      map((customer: any) => dataToCustomer(customer)),
      share()
    );
  }

  public getTransactionById(transactionId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}transaction/${transactionId}`);
  }

  public updateCustomerPlatform(customerId: string): Observable<any> {
    const apiURL = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    const body = { isMobilePlatform: this.platform.is('mobile') };

    return this.http.post(`${apiURL}customers/${customerId}`, body);
  }

  public removeCustomerCard(customerId: string, cardId: string, sharedCards: boolean, isSharedCardsEnabled: boolean): Observable<Object> {
    const apiVer = isSharedCardsEnabled ? 'v2/' : '';
    const shared = isSharedCardsEnabled ? `?shared_cards=${sharedCards}` : '';
    const apiURL = `${environment.apiUrl}${apiVer}${this.routeService.getBaseUrl()}`;
    return this.http.delete(`${apiURL}/customers/${customerId}/cards/${cardId}${shared}`);
  }

  public payWithSavedCard(transactionId: string, cardId: string): Observable<Object> {
    return this.http.post(`${environment.apiUrl}transaction/${transactionId}/card/${cardId}`, {});
  }

  public groupSaveCard(nonce: string, orderId: string, givenName: string, familyName: string): Observable<Object> {
    const sendData = `nonce=${nonce}&orderId=${orderId}&givenName=${givenName}&familyName=${familyName}`;
    return this.http.post(`${environment.apiUrl}api/group_orders/save_card`, sendData, PaymentService.httpOptionsForProcessCard);
  }

  public groupProcessOrder(nonce: string, orderId: string, givenName: string, familyName: string): Observable<Object> {
    const sendData = `nonce=${nonce}&orderId=${orderId}&givenName=${givenName}&familyName=${familyName}`;
    return this.http.post(`${environment.apiUrl}api/group_orders/process_order`, sendData, PaymentService.httpOptionsForProcessCard);
  }

  public groupPayWithSavedCard(groupOrderId: string, cardId: string): Observable<Object> {
    return this.http.post(`${environment.apiUrl}group_orders/${groupOrderId}/card/${cardId}`, {});
  }

  public getVerifyingCode(customerId: string): Observable<Object> {
    return this.http.post(`${environment.apiUrl}customers/${customerId}/twofactor/reset`, {});
  }

  public sendVerifyingCode(customerId: string, code: any): Observable<CustomerToken> {
    const sentData = { entered_pin: code };

    return this.http.post(`${environment.apiUrl}customers/${customerId}/twofactor/verify`, sentData)
      .pipe(
        map((customerToken: any) => {
          return dataToCustomerToken(customerToken);
        })
      );
  }

  public saveCard(nonce: string, transactionId: string, givenName: string, familyName: string, isGiftCard: boolean): Observable<Object> {
    const sendData = `nonce=${nonce}&transactionId=${transactionId}&givenName=${givenName}&familyName=${familyName}&isGiftCard=${isGiftCard}`;
    return this.http.post(environment.apiUrl + 'api/save_card', sendData, PaymentService.httpOptionsForProcessCard);
  }

  public processOrder(nonce: string, transactionId: string, isGiftCard: boolean): Observable<Object> {
    const sendData = `nonce=${nonce}&transactionId=${transactionId}&isGiftCard=${isGiftCard}`;
    return this.http.post(environment.apiUrl + 'api/process_order', sendData, PaymentService.httpOptionsForProcessCard);
  }
  public paymentWithSeveralSources(transactionId: string, sources: PaymentSources): Observable<any> {
    return this.http.post(environment.apiUrl + 'transactions/' + transactionId + '/pay', sources);
  }

  public async getCustomerOpenedTab(): Promise<void> {
    if ((this.cartStore.groupTab || this.cartStore.tab) && !this.paymentStore.customer) {
      await this.initCustomer();
    } else if (await this.storageService.get(STORAGE_CUSTOMER_DATA)) {
      await this.checkCustomerOpenTab();
    }
  }

  public async initCustomer(): Promise<void> {
    if (this.cartStore.groupTab || this.cartStore.tab) {
      let customerId: string;
      if (this.cartStore.groupTab) {
        this.setFeeForGroupTabs();
        customerId = this.cartStore.groupTab.customer_id;
      } else if (this.cartStore.tab) {
        customerId = this.cartStore.tab.customer_id;
      }
      this.getCustomerById(customerId)
        .subscribe((customer: Customer) => {
          this.paymentStore.customer = customer;
          this.cartStore.showTabModal = true;
          this.saveCustomerToStorage();
        });
    }
  }

  public async checkCustomerOpenTab(): Promise<void> {
    if (await this.storageService.get(STORAGE_TAB_DATA)) {
      const { tabId, locationId, isGroup } = await this.storageService.get(STORAGE_TAB_DATA);
      if (isGroup) {
        this.orderApiService.getGroupTabById(tabId)
          .pipe(filter(Boolean))
          .subscribe((tab: GroupOrderTab) => {
            this.cartStore.groupTab = tab;
            this.setFeeForGroupTabs();
            if (!tab.is_closed) {
              if (!this.paymentStore.customer) {
                this.initCustomer();
              }
            } else if (tab.is_closed) {
              this.removeCustomerFromStorage();
            }
          });
      } else {
        this.orderApiService.getTabById(tabId)
          .pipe(filter(Boolean))
          .subscribe((tab: OrderTab) => {
            this.cartStore.tab = tab;
            if (tab.location_id === locationId && !tab.is_closed) {
              if (!this.paymentStore.customer) {
                this.initCustomer();
              }
            } else if (tab.is_closed) {
              this.removeCustomerFromStorage();
            }
          });
      }
    }
  }

  public setFeeForGroupTabs(): void {
    this.cartStore.kyooFee = this.restaurantStore.group.tabsKyooFee;
    this.cartStore.additionalKyooFee = this.restaurantStore.group.tabsAdditionalKyooFee;
  }

  private saveCustomerToStorage(): void {
    let customerData = { token: '', phone: this.paymentStore.customer, orderBotUserId: this.paymentStore.customer.orderBotUserId };
    this.storageService.set(STORAGE_CUSTOMER_DATA, customerData);
    if (this.cartStore.groupTab && this.cartStore.groupTab.is_closed) {
      this.storageService.remove(STORAGE_TAB_DATA);
    }
    if (this.cartStore.tab && this.cartStore.tab.is_closed) {
      this.storageService.remove(STORAGE_TAB_DATA);
    }
  }

  private removeCustomerFromStorage(): void {
    this.cartStore.showTabModal = true;
    this.storageService.remove(STORAGE_TAB_DATA);
  }
}
